import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signInWithPopup,
    GoogleAuthProvider,
    signOut,
    sendPasswordResetEmail,
    sendEmailVerification,
    updateProfile,
    deleteUser,
    User
  } from 'firebase/auth';
  import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
  import { auth, db } from './firebase';
  import { UserProfile } from '../types';
  import { lockEverything } from './vault/vaultManager';
  
  const googleProvider = new GoogleAuthProvider();

  // Strip HTML tags, control characters, and limit length to prevent XSS via display names
  // imported from OAuth providers or user input at registration.
  const sanitizeDisplayName = (name: string | null | undefined): string => {
    if (!name) return '';
    return name
      .replace(/<[^>]*>/g, '')          // strip HTML tags
      .replace(/[\x00-\x1F\x7F]/g, '')  // strip control characters
      .replace(/[&<>"'`]/g, '')         // strip quote and angle chars for defense in depth
      .trim()
      .slice(0, 80);                    // reasonable max length for a display name
  };

  const getDefaultSubscription = (): UserProfile['subscription'] => ({
    plan: 'free',
    status: 'active',
  });

  /**
   * Start the server-authoritative Ultra trial immediately after a teacher profile
   * is created. The initial profile must remain Free because Firestore rules reject
   * client-authored subscription elevation; the authenticated function performs
   * the guarded transition and returns the dates for the in-memory profile.
   */
  export const startAutomaticTeacherTrial = async (user: User): Promise<UserProfile['subscription']> => {
    const idToken = await user.getIdToken();
    const response = await fetch('/.netlify/functions/start-trial', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
    });
    const result = await response.json().catch(() => ({})) as {
      trialStartedAt?: string;
      trialEndsAt?: string;
      error?: string;
    };
    if (!response.ok || !result.trialStartedAt || !result.trialEndsAt) {
      throw new Error(result.error || 'Could not activate the Ultra trial');
    }
    return {
      // MUST match what start-trial.ts writes ('ultra'). Returning 'pro' here
      // gated the whole first session at Pro limits while every banner said
      // Ultra, until the next Firestore snapshot happened to correct it.
      plan: 'ultra',
      status: 'trialing',
      trialStartedAt: result.trialStartedAt,
      trialEndsAt: result.trialEndsAt,
      hasUsedTrial: true,
    };
  };

  const createUserProfile = async (
    user: User,
    role: 'teacher' | 'student' | 'board_admin',
    displayName?: string
  ): Promise<UserProfile> => {
    const rawName = displayName || user.displayName || user.email?.split('@')[0] || 'User';
    const safeName = sanitizeDisplayName(rawName) || 'User';
    let profile: UserProfile = {
      uid: user.uid,
      email: user.email || '',
      displayName: safeName,
      role,
      ...(role === 'teacher' ? { autoTrialPending: true } : {}),
      createdAt: new Date().toISOString(),
      subscription: getDefaultSubscription(),
      settings: {
        theme: 'classic'
      },
      aiUsage: {
        billingPeriodStart: new Date().toISOString().slice(0, 10),
        count: 0
      }
    };
  
    await setDoc(doc(db, 'users', user.uid), {
      ...profile,
      updatedAt: serverTimestamp()
    });

    if (role === 'teacher') {
      try {
        const subscription = await startAutomaticTeacherTrial(user);
        const { autoTrialPending: _consumed, ...activatedProfile } = profile;
        profile = { ...activatedProfile, subscription };
      } catch (error) {
        // The account is already valid and must not become an unrecoverable
        // "email already in use" error. Keep autoTrialPending on the user doc;
        // AuthContext retries it on the next profile snapshot/sign-in.
        console.error('Automatic Ultra trial activation failed; queued for retry:', error);
      }
    }
  
    return profile;
  };
  
  export const getUserProfile = async (uid: string): Promise<UserProfile | null> => {
    const docRef = doc(db, 'users', uid);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return docSnap.data() as UserProfile;
    }
    return null;
  };
  
  export const registerWithEmail = async (
    email: string,
    password: string,
    role: 'teacher' | 'student' | 'board_admin',
    displayName: string
  ): Promise<UserProfile> => {
    const safeDisplayName = sanitizeDisplayName(displayName) || 'User';
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    await updateProfile(user, { displayName: safeDisplayName });

    // Email/password accounts start unverified. Send the verification link now so
    // teachers have a path to verify — the Ultra trial itself starts immediately, but
    // spending trial AI credits requires a verified email (anti-abuse, enforced
    // server-side). Non-fatal: a failed send must not break signup.
    if (role === 'teacher') {
      try { await sendEmailVerification(user); } catch (e) { console.error('Verification email send failed (non-fatal):', e); }
    }

    const profile = await createUserProfile(user, role, safeDisplayName);
    return profile;
  };

  /**
   * Re-send the email-verification link to the signed-in user (e.g. from a
   * "verify to use AI on your trial" prompt). Reloads first so a user who has
   * already verified in another tab isn't sent a redundant email.
   */
  export const resendVerificationEmail = async (): Promise<'sent' | 'already_verified'> => {
    const user = auth.currentUser;
    if (!user) throw new Error('You must be signed in to resend a verification email.');
    await user.reload();
    if (user.emailVerified) return 'already_verified';
    await sendEmailVerification(user);
    return 'sent';
  };
  
  export const loginWithEmail = async (
    email: string,
    password: string
  ): Promise<UserProfile> => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const profile = await getUserProfile(userCredential.user.uid);
    
    if (!profile) {
      throw new Error('User profile not found. Please contact support.');
    }
    
    return profile;
  };
  
  // Initiates Google sign-in — always uses popup (redirect breaks on iOS Safari due to ITP)
  export const startGoogleSignIn = async (): Promise<{ needsRole: boolean }> => {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;
    const profile = await getUserProfile(user.uid);
    return { needsRole: !profile };
  };

  // Creates profile for a user who has already authenticated via Google
  export const completeGoogleSignIn = async (
    role: 'teacher' | 'student' | 'board_admin'
  ): Promise<UserProfile> => {
    const user = auth.currentUser;

    if (!user) {
      throw new Error('No authenticated user found');
    }

    let profile = await getUserProfile(user.uid);

    if (!profile) {
      profile = await createUserProfile(user, role);
    }

    return profile;
  };
  
  export const logout = async (): Promise<void> => {
    /**
     * Lock the vault BEFORE signing out, and do it even if that fails.
     *
     * The vault caches each unlocked class key in IndexedDB so a teacher is not asked for their passphrase
     * every session. Leaving it after sign-out means the next person to use the browser — a colleague on a
     * shared staffroom machine, a student on a classroom laptop — signs in to their own account while the
     * key for someone else's class is still in this origin's storage. The session is keyed by uid so they
     * could not USE it, but the right place for a key whose owner has left is nowhere.
     *
     * Wrapped, so a storage failure cannot prevent the sign-out itself: a teacher who presses log out must
     * end up logged out.
     */
    try {
      await lockEverything();
    } catch (err) {
      console.error('vault: could not clear cached keys on sign-out', err);
    }
    await signOut(auth);
  };
  
  export const resetPassword = async (email: string): Promise<void> => {
    await sendPasswordResetEmail(auth, email);
  };
  
  export const updateUserProfile = async (
    uid: string,
    updates: Partial<UserProfile>
  ): Promise<void> => {
    const docRef = doc(db, 'users', uid);
    const safeUpdates = { ...updates };
    if (typeof safeUpdates.displayName === 'string') {
      safeUpdates.displayName = sanitizeDisplayName(safeUpdates.displayName) || 'User';
    }
    await setDoc(docRef, { ...safeUpdates, updatedAt: serverTimestamp() }, { merge: true });
  };
  
  export const updateUserSettings = async (
    uid: string,
    settings: Partial<UserProfile['settings']>
  ): Promise<void> => {
    const docRef = doc(db, 'users', uid);
    const currentDoc = await getDoc(docRef);
    
    if (currentDoc.exists()) {
      const currentSettings = currentDoc.data().settings || {};
      await setDoc(
        docRef,
        { settings: { ...currentSettings, ...settings }, updatedAt: serverTimestamp() },
        { merge: true }
      );
    }
  };
  
  // Delete the Firebase auth account (call after deleting user data)
  export const deleteAccount = async (): Promise<void> => {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('No authenticated user found');
    }
    await deleteUser(user);
  };
